import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ROICalculatorForm = ({ onCalculate, isCalculating }) => {
  const [formData, setFormData] = useState({
    currentHeadcount: '',
    desiredHeadcount: '',
    averageSalary: '',
    operationalCosts: '',
    serviceType: '',
    location: '',
    timeline: ''
  });

  const [errors, setErrors] = useState({});

  const serviceTypes = [
    { value: 'software-development', label: 'Software Development' },
    { value: 'data-analytics', label: 'Data Analytics & AI' },
    { value: 'customer-support', label: 'Customer Support' },
    { value: 'finance-accounting', label: 'Finance & Accounting' },
    { value: 'hr-operations', label: 'HR Operations' },
    { value: 'research-development', label: 'Research & Development' },
    { value: 'digital-marketing', label: 'Digital Marketing' },
    { value: 'quality-assurance', label: 'Quality Assurance' }
  ];

  const locations = [
    { value: 'bangalore', label: 'Bangalore', description: 'Tech hub with premium talent' },
    { value: 'hyderabad', label: 'Hyderabad', description: 'Emerging tech center' },
    { value: 'pune', label: 'Pune', description: 'Automotive & IT excellence' },
    { value: 'chennai', label: 'Chennai', description: 'Manufacturing & services' },
    { value: 'mumbai', label: 'Mumbai', description: 'Financial services hub' },
    { value: 'delhi-ncr', label: 'Delhi NCR', description: 'Government & enterprise' },
    { value: 'kochi', label: 'Kochi', description: 'Cost-effective operations' },
    { value: 'ahmedabad', label: 'Ahmedabad', description: 'Emerging talent pool' }
  ];

  const timelines = [
    { value: '6-months', label: '6 Months', description: 'Rapid deployment' },
    { value: '12-months', label: '12 Months', description: 'Standard timeline' },
    { value: '18-months', label: '18 Months', description: 'Comprehensive setup' },
    { value: '24-months', label: '24+ Months', description: 'Phased approach' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentHeadcount || formData.currentHeadcount <= 0) {
      newErrors.currentHeadcount = 'Current headcount is required';
    }
    
    if (!formData.desiredHeadcount || formData.desiredHeadcount <= 0) {
      newErrors.desiredHeadcount = 'Desired headcount is required';
    }
    
    if (!formData.averageSalary || formData.averageSalary <= 0) {
      newErrors.averageSalary = 'Average salary is required';
    }
    
    if (!formData.operationalCosts || formData.operationalCosts <= 0) {
      newErrors.operationalCosts = 'Operational costs are required';
    }
    
    if (!formData.serviceType) {
      newErrors.serviceType = 'Service type is required';
    }
    
    if (!formData.location) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.timeline) {
      newErrors.timeline = 'Timeline is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onCalculate(formData);
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 shadow-executive">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 gradient-executive rounded-lg flex items-center justify-center">
          <Icon name="Calculator" size={20} color="white" />
        </div>
        <div>
          <h3 className="font-headline font-semibold text-lg text-text-primary">
            GCC ROI Calculator
          </h3>
          <p className="text-text-secondary text-sm">
            Calculate your potential savings and ROI
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Current Headcount"
            type="number"
            placeholder="e.g., 50"
            value={formData.currentHeadcount}
            onChange={(e) => handleInputChange('currentHeadcount', e.target.value)}
            error={errors.currentHeadcount}
            description="Number of employees in current operations"
            required
          />

          <Input
            label="Desired GCC Headcount"
            type="number"
            placeholder="e.g., 100"
            value={formData.desiredHeadcount}
            onChange={(e) => handleInputChange('desiredHeadcount', e.target.value)}
            error={errors.desiredHeadcount}
            description="Target team size for GCC operations"
            required
          />

          <Input
            label="Average Annual Salary (USD)"
            type="number"
            placeholder="e.g., 80000"
            value={formData.averageSalary}
            onChange={(e) => handleInputChange('averageSalary', e.target.value)}
            error={errors.averageSalary}
            description="Current average salary per employee"
            required
          />

          <Input
            label="Annual Operational Costs (USD)"
            type="number"
            placeholder="e.g., 500000"
            value={formData.operationalCosts}
            onChange={(e) => handleInputChange('operationalCosts', e.target.value)}
            error={errors.operationalCosts}
            description="Infrastructure, tools, and overhead costs"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Service Type"
            placeholder="Select service type"
            options={serviceTypes}
            value={formData.serviceType}
            onChange={(value) => handleInputChange('serviceType', value)}
            error={errors.serviceType}
            searchable
            required
          />

          <Select
            label="Preferred Location"
            placeholder="Select location"
            options={locations}
            value={formData.location}
            onChange={(value) => handleInputChange('location', value)}
            error={errors.location}
            searchable
            required
          />

          <Select
            label="Implementation Timeline"
            placeholder="Select timeline"
            options={timelines}
            value={formData.timeline}
            onChange={(value) => handleInputChange('timeline', value)}
            error={errors.timeline}
            required
          />
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <h4 className="font-medium text-text-primary mb-1">Calculation Methodology</h4>
              <p className="text-sm text-text-secondary">
                Our ROI calculations are based on industry benchmarks, real market data, and proven cost structures from 200+ successful GCC implementations. Results include setup costs, operational savings, and 5-year projections.
              </p>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isCalculating}
          className="bg-cta hover:bg-cta/90 text-cta-foreground"
          iconName="TrendingUp"
          iconPosition="left"
        >
          {isCalculating ? 'Calculating ROI...' : 'Calculate ROI & Savings'}
        </Button>
      </form>
    </div>
  );
};

export default ROICalculatorForm;